import { useMemo, Children } from 'react';
import { f as flattenChildren } from '../unsorted/index3.js';
import { isChildSubcomponent } from '../utils/subcomponentHelpers.js';

function isChildPartOfSubcomponentCollection(child, subcomponents) {
  var isSub = subcomponents.some(function (component) {
    return isChildSubcomponent(child, component);
  });
  return isSub;
}

/**
 * This function separates React children into subcomponents and other children based on a specified
 * subcomponent type.
 * @param {ReactNode} children - `children` is a prop that is passed to a React component and
 * represents the child elements that are nested within the component. It can be a single child element
 * or an array of child elements.
 * @param subcomponent - The `subcomponent` parameter is a React component type that is expected to be
 * a child of the `children` parameter. The function will split the `children` into two arrays: one
 * containing all the `subcomponent` instances, and the other containing all the other children.
 * @returns An object with two properties: `subComponents` and `otherChildren`. `subComponents` is an
 * array of React nodes that are subcomponents of the specified `subcomponent` type, and
 * `otherChildren` is an array of all other React nodes that are not subcomponents of the specified
 * `subcomponent` type.
 */
function useSplitApartChildrenAndSubComponents(children,
// biome-ignore lint/suspicious/noExplicitAny: the props type could really be anyting right now.
subcomponents) {
  var subcomponentsArray = subcomponents;
  if (!Array.isArray(subcomponents)) subcomponentsArray = [subcomponents];
  var splitSubComponents = [];
  var otherChildren = [];
  var flattenedChildren = useMemo(function () {
    return flattenChildren(children);
  }, [children]);
  Children.forEach(flattenedChildren, function (child) {
    if (isChildPartOfSubcomponentCollection(child, subcomponentsArray)) {
      splitSubComponents.push(child);
    } else {
      otherChildren.push(child);
    }
  });
  return {
    subcomponents: splitSubComponents,
    otherChildren: otherChildren.length === 0 ? null : otherChildren.length === 1 ? otherChildren[0] : otherChildren
  };
}
function splitApartChildrenAndSubComponents(children,
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
subcomponents) {
  var subcomponentsArray = subcomponents;
  if (!Array.isArray(subcomponents)) subcomponentsArray = [subcomponents];
  var splitSubComponents = [];
  var otherChildren = [];
  var flattenedChildren = flattenChildren(children);
  Children.forEach(flattenedChildren, function (child) {
    if (isChildPartOfSubcomponentCollection(child, subcomponentsArray)) {
      splitSubComponents.push(child);
    } else {
      otherChildren.push(child);
    }
  });
  return {
    subcomponents: splitSubComponents,
    otherChildren: otherChildren.length === 0 ? null : otherChildren.length === 1 ? otherChildren[0] : otherChildren
  };
}

export { splitApartChildrenAndSubComponents, useSplitApartChildrenAndSubComponents };
//# sourceMappingURL=useSplitApartChildrenAndSubComponents.js.map
